import { list, map, object, primitive, serializable } from "serializr"
import { MapStringAny } from "@/effects/ValueRange"
import ValueMod from "@/effects/ValueMod"
import EffectType from "@/effects/EffectType"
import Effect from "@/effects/Effect"
import Research from "@/Research"
import Laboratory from "@/farm/Laboratory"
import util from "@/util"
import { toRaw } from "vue"

export default class EffectManager {
  @serializable(map(object(Effect)))
  private effects: { [key: string]: Effect }

  constructor() {
    this.effects = {}
  }

  update(dt: number, effectToModsMap: { [key: string]: MapStringAny }) {
    for (let key in this.effects) {
      let effect: Effect = this.effects[key]
      effect.update(dt)

      if (effect.shouldBeRemoved) {
        this.deleteEffectImmediately(effect, effectToModsMap)
      } else {
        effectToModsMap[effect.modName][effect.source] = effect.mod
      }
    }
  }

  /**
   * Replaces or removes any existing effects to match the effects defined on given laboratory.
   * @param lab
   * @param effectToModsMap
   */
  refreshEffects(lab: Laboratory, effectToModsMap: any) {
    for (let key in this.effects) {
      let effect: Effect = this.effects[key]

      // Do not refresh temporary effects
      if (effect.source.substring(0, 4) == "tmp-") continue

      let research = lab.researchById(effect.source)
      let researchMod = research?.effects[effect.modName]

      if (research == undefined || researchMod == undefined) {
        // If the research or mod doesn't exist anymore, just remove the effect
        this.deleteEffectImmediately(effect, effectToModsMap)
      } else {
        // Otherwise update the effect so that it has the same mod and type as the research specifies
        let refreshedEffect = new Effect(effect.source, effect.modName, researchMod, research.effectType)
        refreshedEffect.fractionComplete = effect.fractionComplete
        this.effects[key] = refreshedEffect
      }

      // If the research exists, add any effects that we are not already managing.
      // This makes sure that we will apply any new effects that the research has received.
      if (research != undefined) {
        for (let modKey in research.effects) {
          let newMod = research.effects[modKey]
          let newEffect = new Effect(research.id, modKey, newMod, research.effectType)
          if (!this.isManaged(newEffect)) {
            this.effects[newEffect.id] = newEffect
          }
        }
      }
    }
  }

  /**
   * Adds all effects of a particular research to this manager
   * @param research
   * @param effectToModsMap must be the same as the one given to update function
   */
  addResearch(research: Research, effectToModsMap: { [key: string]: MapStringAny }) {
    for (let effectName in research.effects) {
      // For each effect (like "milkStorage"), see if there is some ValueRange we can apply this effect to
      if (effectToModsMap[effectName]) {
        // Each effect is basically just a ValueMod
        let valueMod = research.effects[effectName]
        // On the ValueRange we should apply to, set a key with the name of the research, and set the effect
        this.addEffect(research.id, effectName, new ValueMod(valueMod.value, valueMod.type), research.effectType)
      }
    }
  }

  removeResearch(research: Research, effectToModsMap: { [key: string]: MapStringAny }) {
    for (let effectName in research.effects) {
      if (effectToModsMap[effectName]) {
        //let valueMod = research.effects[effectName]
        this.removeEffect(research.id, effectName)
      }
    }
  }

  addTempEffect(effectId: string, targetModName: string, mod: ValueMod, type: EffectType) {
    this.addEffect(`tmp-${effectId}`, targetModName, mod, type)
  }

  removeTempEffect(effectId: string, targetModName: string) {
    this.removeEffect(`tmp-${effectId}`, targetModName)
  }

  overview() {
    let effectsBySource: { [key: string]: [Effect] } = {}
    for (let [id, effect] of Object.entries(this.effects)) {
      let source = effect.source
      if (source in effectsBySource) {
        effectsBySource[source].push(effect)
      } else {
        effectsBySource[source] = [effect]
      }
      //console.log(effect.source, effect.modName toRaw(effect))
    }

    for (let [source, effects] of Object.entries(effectsBySource)) {
      console.log(source)
      for (let effect of effects) {
        console.log("---", effect.modString())
      }
    }
    //console.log(effectsBySource)
  }

  private addEffect(researchId: string, effectName: string, mod: ValueMod, type: EffectType) {
    let effect = new Effect(researchId, effectName, mod, type)

    if (this.isManaged(effect) && type.timeType == 'slow') {
      // If we were currently removing the effect, just start adding it back in
      if (this.effects[effect.id].isBeingRemoved) this.effects[effect.id].stopRemoving()

      // If the effect has changed we must update its values
      if (!effect.equals(this.effects[effect.id])) this.effects[effect.id].replace(effect)
    } else {
      this.effects[effect.id] = effect
    }
  }

  private removeEffect(researchName: string, effectName: string) {
    let effectToRemove = new Effect(researchName, effectName, new ValueMod(0, ''), new EffectType())

    // If effect is not managed, don't do anything
    if (!this.isManaged(effectToRemove)) return

    // Otherwise, remove the effect
    let effect = this.effects[effectToRemove.id]
    effect.startRemoving()
  }

  private deleteEffectImmediately(effect: Effect, effectToModsMap: any) {
    delete effectToModsMap[effect.modName][effect.source]
    delete this.effects[effect.id]
  }

  private isManaged(effect: Effect) {
    return effect.id in this.effects
  }
}
